import React from 'react';
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
  Sector
} from 'recharts';
import { Card, Typography, useTheme, Paper, Divider, Box } from '@mui/material';

// Custom active shape for the Pie chart when hovering
const renderActiveShape = (props) => {
  const RADIAN = Math.PI / 180;
  const {
    cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle,
    fill, payload, percent, value, name, documentNames
  } = props;
  
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + (outerRadius + 10) * cos;
  const sy = cy + (outerRadius + 10) * sin;
  const mx = cx + (outerRadius + 30) * cos;
  const my = cy + (outerRadius + 30) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 22;
  const ey = my;
  const textAnchor = cos >= 0 ? 'start' : 'end';

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 6}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        stroke="#fff"
        strokeWidth={2}
      />
      <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
      <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
      <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey - 10} textAnchor={textAnchor} fill="#333" fontSize={12}>{name}</text>
      <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey + 5} textAnchor={textAnchor} fill="#999" fontSize={11}>
        {`Value: ${typeof value === 'number' ? value.toFixed(2) : value}`}
      </text>
      <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey + 20} textAnchor={textAnchor} fill="#999" fontSize={11}>
        {`(${(percent * 100).toFixed(2)}%)`}
      </text>
    </g>
  );
};

// Custom tooltip for hovering over pie slices
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Paper
        elevation={3}
        sx={{
          padding: '12px',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid #ccc',
          borderRadius: '8px',
          maxWidth: '300px',
          boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
          {data.name || data.category}
        </Typography>
        <Divider sx={{ my: 1 }} />
        <Typography variant="body2" sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between' }}>
          <span>Value:</span> <span style={{ fontWeight: 'bold', color: data.fill }}>
            {data.value || data.count}
          </span>
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between' }}>
          <span>Percentage:</span> <span style={{ fontWeight: 'bold', color: data.fill }}>
            {data.percentage ? `${data.percentage.toFixed(2)}%` : 
             (data.total ? `${((data.value / data.total) * 100).toFixed(2)}%` : '0%')}
          </span>
        </Typography>
        
        {data.documentNames && data.documentNames.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" fontWeight="medium">
              Documents: {data.documentNames.length}
            </Typography>
            <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
              {data.documentNames.slice(0, 5).map((name, index) => (
                <Typography 
                  key={index} 
                  variant="caption" 
                  sx={{ 
                    display: 'block',
                    ml: 1,
                    fontSize: '0.75rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  • {name.length > 40 ? '...' + name.slice(-35) : name}
                </Typography>
              ))}
              {data.documentNames.length > 5 && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block',
                    ml: 1,
                    fontSize: '0.75rem',
                    fontStyle: 'italic',
                    color: '#666'
                  }}
                >
                  And {data.documentNames.length - 5} more...
                </Typography>
              )}
            </div>
          </>
        )}
      </Paper>
    );
  }
  return null;
};

// Colors for pie chart segments
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A259FF', '#FF6B6B', '#54A0FF', '#1DD1A1', '#FFC045', '#FF9966'];

const PieChartComponent = ({ data = [], groupBy = "category", valueField = "value" }) => {
  const theme = useTheme();
  const [activeIndex, setActiveIndex] = React.useState(0);
  
  // Process data for pie chart
  const processedData = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // Check if the data is already in the expected format from the server
    // Server format has properties: category, count, percentage
    if (data[0] && data[0].category !== undefined && data[0].count !== undefined) {
      // Data is already in server format - just map to the expected structure for the PieChart
      return data.map((item, index) => ({
        name: item.category,
        value: item.count,
        percentage: item.percentage,
        fill: COLORS[index % COLORS.length]
      }));
    }
    
    // Otherwise use the client-side data processing logic
    // Group data by the specified groupBy field
    const groupedData = data.reduce((acc, item) => {
      const key = item[groupBy] || 'Unknown';
      
      if (!acc[key]) {
        acc[key] = {
          name: key,
          value: 0,
          documentNames: []
        };
      }
      
      // Add the value
      acc[key].value += parseFloat(item[valueField] || 0);
      
      // Add document reference if available
      if (item.documentNames) {
        acc[key].documentNames = [...acc[key].documentNames, ...item.documentNames];
      }
      
      return acc;
    }, {});
    
    // Convert to array and sort by value
    const result = Object.values(groupedData)
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value);
    
    // Calculate total for percentages
    const total = result.reduce((sum, item) => sum + item.value, 0);
    result.forEach((item, index) => {
      item.total = total; // Add total for percentage calculation
      item.fill = COLORS[index % COLORS.length]; // Add fill color
    });
    
    return result;
  }, [data, groupBy, valueField]);
  
  // Handle pie segment hover
  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  if (processedData.length === 0) {
    return (
      <Card
        sx={{ 
          boxShadow: '0 6px 10px rgba(0,0,0,0.15)', 
          borderRadius: 2,
          p: 4, 
          backgroundColor: 'white',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
        }}
      >
        <Typography variant="h6" color="text.secondary">
          No data available for chart
        </Typography>
      </Card>
    );
  }

  // Determine title based on data format
  const chartTitle = processedData[0].category !== undefined ? 'Category Distribution' : 
                    `${groupBy.charAt(0).toUpperCase() + groupBy.slice(1)} Distribution`;

  return (
    <Card
      sx={{ 
        boxShadow: '0 6px 10px rgba(0,0,0,0.15)', 
        borderRadius: 2,
        p: 2, 
        backgroundColor: 'white',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
        }
      }}
    >
      <Box sx={{ width: '100%', textAlign: 'center', mb: 2 }}>
        <Typography variant="h6" color="text.primary">
          {chartTitle}
        </Typography>
      </Box>
      
      <ResponsiveContainer width="100%" height={400}>
        <RechartsPieChart>
          <Pie
            activeIndex={activeIndex}
            activeShape={renderActiveShape}
            data={processedData}
            cx="50%"
            cy="50%"
            innerRadius={70}
            outerRadius={120}
            dataKey="value"
            nameKey="name"
            onMouseEnter={onPieEnter}
            paddingAngle={2}
          >
            {processedData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={entry.fill || COLORS[index % COLORS.length]} 
                stroke={theme.palette.background.paper}
                strokeWidth={2}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            layout="horizontal" 
            verticalAlign="bottom" 
            align="center"
            iconType="circle"
            iconSize={10}
            formatter={(value, entry) => {
              // Use category field if available (server format) or name field (client format)
              return <span style={{ color: entry.color }}>{entry.payload.category || entry.payload.name}</span>;
            }}
            wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
          />
        </RechartsPieChart>
      </ResponsiveContainer>
    </Card>
  );
};

export default PieChartComponent;